import type { ParsedLocation } from "@tanstack/react-router";

import type { RootRouteContext } from "@/routes/-root/-root-context";
import {
  guardCafDocumentsReprovado,
  guardGeneralTerms,
  guardInvalidPerfil,
  guardMissingFlows,
  guardMissingPaymentCard,
} from "@/routes/-root/root-guards-navigation";
import { guardAppVersion, guardInactiveUser } from "@/routes/-root/root-guards-session";

export interface GuardParams {
  context: RootRouteContext;
  location: ParsedLocation;
}

export type RouteGuard = (params: GuardParams) => Promise<void> | void;

export interface GuardEntry {
  guard: RouteGuard;
  protectedPaths?: string[];
}

/**
 * Executa os guards em ordem de prioridade.
 * Se o pathname atual pertence a um guard de índice `j`, os guards seguintes são ignorados,
 * evitando que guards de menor prioridade redirecionem o usuário para fora de uma rota
 * já gerenciada. Guards anteriores (maior prioridade) sempre executam e podem sobrescrever.
 */
export async function runGuards(
  guards: GuardEntry[],
  params: GuardParams,
): Promise<void> {
  const all = [...newSessionGuards, ...routeGuards];

  const ownerIndex = all.findIndex((e) =>
    e.protectedPaths?.some((p) => params.location.pathname.startsWith(p)),
  );

  for (const guard of guards) {
    if (ownerIndex !== -1 && all.indexOf(guard) > ownerIndex) break;
    await guard.guard(params);
  }
}

/**
 * Executado apenas quando uma nova sessão de usuário é iniciada (primeiro carregamento ou troca de usuário).
 * Para adicionar uma nova restrição de sessão, inclua um GuardEntry aqui.
 */
export const newSessionGuards: GuardEntry[] = [
  { guard: guardAppVersion, protectedPaths: ["/update-required"] },
  { guard: guardInactiveUser, protectedPaths: ["/access-blocked"] },
];

/**
 * Executado em toda navegação autenticada, em ordem de prioridade.
 * Para adicionar uma nova restrição, crie uma função RouteGuard e inclua-a aqui.
 */
export const routeGuards: GuardEntry[] = [
  { guard: guardInvalidPerfil },
  { guard: guardGeneralTerms, protectedPaths: ["/general-terms"] },
  { guard: guardMissingPaymentCard, protectedPaths: ["/add-payment-card"] },
  { guard: guardMissingFlows, protectedPaths: ["/flows"] },
  { guard: guardCafDocumentsReprovado, protectedPaths: ["/caf-documents-blocked"] },
];
